import React from 'react';
import {
  Typography,
  Box,
  Paper,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Divider,
  Button,
  LinearProgress,
  Grid,
  Chip,
  Card,
  CardContent,
} from '@mui/material';
import EmailIcon from '@mui/icons-material/Email';
import PersonIcon from '@mui/icons-material/Person';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import StarIcon from '@mui/icons-material/Star';
import SubscriptionsIcon from '@mui/icons-material/Subscriptions';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { formatDate } from '../../../../../utils/dateUtils';

// Interfaces
interface Contact {
  id: string;
  firstName: string;
  lastName: string;
  email: string;
  phone?: string;
  company?: string;
  title?: string;
  tags: string[];
}

interface EmailTemplate {
  subject: string;
  body: string;
  includeCalendarInvite: boolean;
  includeSubscriptionDetails: boolean;
}

interface SubscriptionTier {
  id: string;
  name: string;
  description: string;
  price: number;
  interval: 'monthly' | 'annual';
  features: string[];
  isPublic: boolean;
  position: number;
  maxMembers?: number;
  trialDays?: number;
  status: 'active' | 'archived' | 'draft';
  coverImageUrl?: string;
  benefits?: string[];
  created?: Date;
  modified?: Date;
}

interface SubscriptionReviewStepProps {
  contacts: Contact[];
  subscription: SubscriptionTier | null;
  emailTemplate: EmailTemplate;
  onSend: () => void;
  sending: boolean;
  sendingProgress: number;
}

const SubscriptionReviewStep: React.FC<SubscriptionReviewStepProps> = ({
  contacts,
  subscription,
  emailTemplate,
  onSend,
  sending,
  sendingProgress,
}) => {
  // Format the email body with variables replaced for preview
  const formatEmailBody = (body: string, contact: Contact) => {
    let formattedBody = body
      .replace(/\{\{firstName\}\}/g, contact.firstName)
      .replace(/\{\{lastName\}\}/g, contact.lastName)
      .replace(/\{\{email\}\}/g, contact.email)
      .replace(/\{\{organizationName\}\}/g, 'Pangia')
      .replace(/\n/g, '<br/>');
    
    return formattedBody;
  };

  return (
    <Box>
      <Typography variant="h6" gutterBottom>
        Review and Confirm
      </Typography>
      
      <Grid container spacing={3}>
        {/* Left column: Subscription details */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" gutterBottom>
            Subscription Details
          </Typography>
          
          <Card sx={{ mb: 3 }}>
            {subscription?.coverImageUrl && (
              <Box 
                component="img" 
                src={subscription.coverImageUrl} 
                alt={subscription.name}
                sx={{ 
                  width: '100%', 
                  height: 160, 
                  objectFit: 'cover',
                  borderBottom: '1px solid rgba(0,0,0,0.12)'
                }}
              />
            )}
            
            <CardContent>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="h6" component="div">
                  {subscription?.name || 'No subscription selected'}
                </Typography>
                <Chip 
                  color="primary" 
                  size="small" 
                  label={`$${subscription?.price.toFixed(2)}/${subscription?.interval}`}
                  icon={<AttachMoneyIcon />}
                />
              </Box>
              
              <Typography variant="body2" color="text.secondary" paragraph>
                {subscription?.description}
              </Typography>
              
              <Divider sx={{ my: 2 }} />
              
              <Typography variant="subtitle2" gutterBottom>
                Features:
              </Typography>
              
              <List dense disablePadding>
                {subscription?.features.map((feature, index) => (
                  <ListItem key={index} disablePadding sx={{ py: 0.5 }}>
                    <CheckCircleIcon color="success" fontSize="small" sx={{ mr: 1 }} />
                    <ListItemText primary={feature} />
                  </ListItem>
                ))}
              </List>
              
              {subscription?.trialDays && (
                <Box sx={{ mt: 2, display: 'flex', alignItems: 'center' }}>
                  <CalendarTodayIcon fontSize="small" sx={{ mr: 1 }} />
                  <Typography variant="body2">
                    {subscription.trialDays}-day free trial included
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Grid>
        
        {/* Right column: Email preview and contacts */}
        <Grid item xs={12} md={6}>
          <Typography variant="subtitle1" gutterBottom>
            Email Preview
          </Typography>
          
          <Paper sx={{ p: 2, mb: 3, bgcolor: 'background.paper' }}>
            <Typography variant="subtitle2" gutterBottom>
              Subject: {emailTemplate.subject}
            </Typography>
            
            <Divider sx={{ my: 1 }} />
            
            <Typography variant="body2" sx={{ whiteSpace: 'pre-line' }}>
              <div dangerouslySetInnerHTML={{ __html: formatEmailBody(emailTemplate.body, contacts[0]) }} />
            </Typography>
            
            {emailTemplate.includeSubscriptionDetails && subscription && (
              <Box sx={{ mt: 2, p: 2, bgcolor: '#f5f5f5', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Subscription Details:
                </Typography>
                <Typography variant="body2">
                  <strong>Plan:</strong> {subscription.name}<br />
                  <strong>Price:</strong> ${subscription.price.toFixed(2)}/{subscription.interval}<br />
                  {subscription.trialDays && (
                    <><strong>Trial Period:</strong> {subscription.trialDays} days<br /></>
                  )}
                </Typography>
              </Box>
            )}
            
            <Box sx={{ mt: 2, textAlign: 'center' }}>
              <Button
                variant="contained"
                color="primary"
                disabled
                startIcon={<SubscriptionsIcon />}
                sx={{ textTransform: 'none' }}
              >
                Complete Subscription
              </Button>
              <Typography variant="caption" display="block" color="text.secondary" sx={{ mt: 1 }}>
                Email will include this button for recipients
              </Typography>
            </Box>
          </Paper>
          
          <Typography variant="subtitle1" gutterBottom>
            Recipients ({contacts.length})
          </Typography>
          
          <Paper sx={{ maxHeight: 200, overflow: 'auto' }}>
            <List dense>
              {contacts.map((contact) => (
                <ListItem key={contact.id}>
                  <ListItemAvatar>
                    <Avatar>
                      <PersonIcon />
                    </Avatar>
                  </ListItemAvatar>
                  <ListItemText 
                    primary={`${contact.firstName} ${contact.lastName}`} 
                    secondary={contact.email}
                  />
                </ListItem>
              ))}
            </List>
          </Paper>
        </Grid>
      </Grid>
      
      {sending && (
        <Box sx={{ width: '100%', mt: 4 }}>
          <Typography variant="body2" color="text.secondary" align="center" gutterBottom>
            Sending invitation emails... {sendingProgress}%
          </Typography>
          <LinearProgress 
            variant="determinate" 
            value={sendingProgress} 
            sx={{ height: 8, borderRadius: 4 }}
          />
        </Box>
      )}
      
      <Box sx={{ mt: 4, display: 'flex', justifyContent: 'flex-end' }}>
        <Button
          variant="contained"
          color="primary"
          disabled={sending || !subscription}
          onClick={onSend}
          startIcon={<EmailIcon />}
        >
          {contacts.length > 1 
            ? `Send ${contacts.length} Invitations` 
            : 'Send Invitation'}
        </Button>
      </Box>
    </Box>
  );
};

export default SubscriptionReviewStep;
